import { Injectable, inject, signal, computed } from '@angular/core';
import { Router } from '@angular/router';
import { of, map, catchError, Observable } from 'rxjs';
import { AuthService } from '../../../core/services/auth.service';
import { AuthError, AuthState, AuthUser, LoginRequest, MenuItem } from '../../../core/models/auth.model';
import { ACCESS_DENIED_ROUTE, getFirstAvailableRoute, hasRoutePermission } from '../../../core/guards/permission.guard';

@Injectable({ providedIn: 'root' })
export class AuthFacade {
  private readonly authService = inject(AuthService);
  private readonly router = inject(Router);

  constructor() {
    if (typeof window !== 'undefined') {
      window.addEventListener('focus', () => {
        if (this.isAuthenticated()) {
          this.checkSessionStatus().subscribe();
        }
      });
    }
  }

  // ── State (private writable signals) ──────────────────────────────────────
  private readonly _state = signal<AuthState>(this.getInitialState());

  // ── Public read-only selectors ─────────────────────────────────────────────
  readonly isLoading = computed(() => this._state().isLoading);
  readonly error = computed(() => this._state().error);
  readonly user = computed(() => this._state().user);
  readonly authUser = computed(() => {
    if (typeof window !== 'undefined' && window.localStorage) {
      const raw = localStorage.getItem('auth_user');
      if (raw) {
        try {
          return JSON.parse(raw) as AuthUser;
        } catch {
          return null;
        }
      }
    }
    return null;
  });
  readonly isAuthenticated = computed(() => !!this._state().token);

  private getInitialState(): AuthState {
    if (typeof window !== 'undefined' && window.localStorage) {
      const token = localStorage.getItem('token');
      const userStr = localStorage.getItem('user');
      if (token && userStr) {
        try {
          return {
            user: JSON.parse(userStr),
            token,
            isLoading: false,
            error: null,
          };
        } catch (e) {
          // ignore parsing error
        }
      }
    }
    return {
      user: null,
      token: null,
      isLoading: false,
      error: null,
    };
  }

  // ── Actions ────────────────────────────────────────────────────────────────
  login(email: string, password: string, redirectUrl?: string): void {
    const credentials: LoginRequest = { email, password };

    this._state.update(s => ({ ...s, isLoading: true, error: null }));

    this.authService.login(credentials).subscribe({
      next: (response) => {
        if (typeof window !== 'undefined' && window.localStorage) {
          localStorage.setItem('token', response.token);
          localStorage.setItem('auth_user', JSON.stringify(response.user));
        }

        this.authService.getProfileByIdentityId(response.user.idUser).pipe(
          map((profileRes) => {
            const profile = profileRes?.data;
            if (profile) {
              return {
                ...profile,
                role: response.user.role,
                idStateFk: response.user.idStateFk,
                accountStatus: response.user.accountStatus,
              };
            }
            return response.user;
          }),
          catchError(() => of(response.user))
        ).subscribe((finalUser) => {
          if (typeof window !== 'undefined' && window.localStorage) {
            localStorage.setItem('user', JSON.stringify(finalUser));
          }

          this._state.set({
            user: finalUser,
            token: response.token,
            isLoading: false,
            error: null,
          });

          const targetUrl = this.resolveLandingUrl(response.menu, redirectUrl);
          this.router.navigateByUrl(targetUrl);
        });
      },
      error: (err: AuthError) => {
        this._state.update(s => ({
          ...s,
          isLoading: false,
          error: err,
        }));
      },
    });
  }

  private resolveLandingUrl(menu: MenuItem[], redirectUrl?: string): string {
    if (redirectUrl && redirectUrl !== '/dashboard' && redirectUrl !== '/') {
      if (hasRoutePermission(menu, redirectUrl)) {
        return redirectUrl;
      }
    }

    if (hasRoutePermission(menu, '/dashboard')) {
      return '/dashboard';
    }

    const firstRoute = getFirstAvailableRoute(menu);
    return firstRoute ?? ACCESS_DENIED_ROUTE;
  }

  logout(): void {
    this.authService.logout().subscribe({
      next: () => this.clearLocalSessionAndRedirect(),
      error: () => this.clearLocalSessionAndRedirect()
    });
  }

  forceLogout(sessionExpired: boolean = false): void {
    this.clearLocalSessionAndRedirect(sessionExpired);
  }

  checkSessionStatus(): Observable<boolean> {
    const authUser = this.authUser();
    if (!authUser?.idUser) {
      return of(true);
    }
    return this.authService.getUserIdentityStatus(authUser.idUser).pipe(
      map((response) => {
        if (response?.state && response?.data) {
          if (response.data.idStateFk && response.data.idStateFk !== 1) {
            this.forceLogout(true);
            return false;
          }
        }
        return true;
      }),
      catchError((error) => {
        if (error?.status === 401 || error?.status === 404) {
          this.forceLogout(true);
          return of(false);
        }
        return of(true);
      })
    );
  }

  private clearLocalSessionAndRedirect(sessionExpired: boolean = false): void {
    if (typeof window !== 'undefined' && window.localStorage) {
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      localStorage.removeItem('auth_user');
      localStorage.removeItem('menu');
    }
    this._state.set({ user: null, token: null, isLoading: false, error: null });
    this.router.navigate(['/auth/login'], sessionExpired ? { queryParams: { expired: true } } : undefined);
  }
}
